import { useMemo, useState } from "react";
import { MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  ENTITY_NAMES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IOrderTemplateResponse, IVerticalTabProps } from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import {
  useOrderTemplate,
  useOrderTemplates,
  usePermissions,
} from "@mibuildweb/hooks";
import { ConfigOrderTemplateCard } from "./components/ConfigOrderTemplateCard";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";

const tabs = [
  {
    label: "All",
    value: "All",
    filter: {},
  },
  {
    label: "Active",
    value: "Active",
    filter: { isActive: true },
  },
  {
    label: "Draft",
    value: "Draft",
    filter: { isActive: false },
  },
];

export function ConfigOrderTemplateList() {
  const { navigate } = useRouter();

  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedOrderTemplate, setSelectedOrderTemplate] =
    useState<IOrderTemplateResponse | null>(null);

  const debouncedSearch = useDebounce(search);

  const { hasPermission } = usePermissions();
  const canCreateOrderTemplate = hasPermission(
    PERMISSIONS.ORDER_TEMPLATE.CREATE
  );
  const canDeleteOrderTemplate = hasPermission(
    PERMISSIONS.ORDER_TEMPLATE.DELETE
  );
  const canUpdateOrderTemplate = hasPermission(
    PERMISSIONS.ORDER_TEMPLATE.UPDATE
  );

  const {
    isLoading,
    setOrderTemplates,
    orderTemplates,
    recentOrderTemplates,
    setRecentOrderTemplates,
    getOrderTemplates,
    getRecentOrderTemplates,
    filter,
    setFilter,
  } = useOrderTemplates();

  const { deleteOrderTemplate, updateOrderTemplate } = useOrderTemplate();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleOpenDeleteModal = (item: IOrderTemplateResponse) => {
    setSelectedOrderTemplate(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedOrderTemplate) {
      deleteOrderTemplate(selectedOrderTemplate, {
        onSuccess: () => {
          setRefresh((prev) => !prev);
        },
      });
      setSelectedOrderTemplate(null);
      setOpenDeleteModal(false);
    }
  };

  const updateDraftStatus = (id: string, name: string, isActive: boolean) => {
    updateOrderTemplate(
      id,
      { isActive },
      {
        onSuccess: () => {
          setRefresh((prev) => !prev);
        },
        name,
      }
    );
  };

  const navigateToAddOrderTemplate = () => {
    navigate(`${CONFIG_PATHS[CONFIG_PAGES.ORDER_TEMPLATES]}/add-order-type`);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IOrderTemplateResponse[]> | null
  ) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const refreshData = useMemo(() => ({ refresh, filter }), [refresh, filter]);

  return (
    <ConfigPageLayout
      title={ENTITY_NAMES.ORDER_TEMPLATES}
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      titleActionComponent={
        canCreateOrderTemplate && (
          <AddButton
            label={`Add ${ENTITY_NAMES.ORDER_TEMPLATE}`}
            onClick={navigateToAddOrderTemplate}
          />
        )
      }
      loading={isLoading}
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={tabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={orderTemplates ?? []}
          dataProvider={getOrderTemplates}
          setData={setOrderTemplates}
          loading={isLoading}
          CardComponent={ConfigOrderTemplateCard}
          cardProps={{
            handleOpenDeleteModal,
            updateDraftStatus,
            canDeleteOrderTemplate,
            canUpdateOrderTemplate,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          recentData={recentOrderTemplates}
          setRecentData={setRecentOrderTemplates}
          recentDataProvider={getRecentOrderTemplates}
          entityName={ENTITY_NAMES.ORDER_TEMPLATES}
          emptyListProps={{
            text: `No ${ENTITY_NAMES.ORDER_TEMPLATES} found`,
          }}
          refresh={refreshData}
        />
      </VerticalTabsLayout>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedOrderTemplate?.name}. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
}
